// utils/NotificationService.ts

import { NOTIFICATION_EVENT, NotificationType } from '../events/notificationEvent';

export { NotificationType };

/**
 * Service to show notifications in the application.
 * This class provides a static method to display notifications with different types, titles, and messages.
 */
export class NotificationService {
	/**
	 * Shows a notification with the specified type, title, and message.
	 * The notification is dynamically created and added to the notification container.
	 * It uses the custom web component <wc-notification> to display the notification content.
	 *
	 * @param {NotificationType} type - The type of the notification (success, error, info).
	 * Determines the styling and accessibility attributes of the notification.
	 * @param {string} title - The title of the notification.
	 * Displayed prominently at the top of the notification.
	 * @param {string} message - The message of the notification.
	 * Provides additional context or information within the notification.
	 */
	static showNotification(type: NotificationType, title: string, message: string): void {
		// Create a new instance of the wc-notification component
		const notification = document.createElement('wc-notification');

		// Create a JSON object with explicitly named keys
		const content = JSON.stringify({
			title: title,
			message: message,
			type: type,
		});

		// Attach the JSON string to the element as an attribute
		notification.setAttribute('data-variables', content);

		// Append the notification to the container
		let container = document.querySelector('#notification-container');
		if (!container) {
			container = document.createElement('div');
			container.id = 'notification-container';
			document.body.appendChild(container);
		}
		container.appendChild(notification);
		requestAnimationFrame(() => {
			setTimeout(function () {
				notification.classList.add('wc-notification--active');
			}, 0);
		});
	}

	/**
	 * Listens for notification events and renders them via showNotification.
	 *
	 * Call this once per page, which initialise.ts does from the bundle every page loads. Each call
	 * registers another listener, and every notification is then shown once per listener.
	 */
	static listenForEvents(): void {
		document.addEventListener(NOTIFICATION_EVENT, (event) => {
			const { type, title, message } = event.detail;
			NotificationService.showNotification(type, title, message);
		});
	}
}
